import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Smartphone, Trash2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../utils/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { PinInput } from "./PinInput";
import {
  getPinDevice,
  setPinDevice,
  clearPinDevice,
  PinDevice,
  PIN_DEVICES_CHANGED_EVENT,
} from "../utils/pinDevice";

interface PinDeviceInfo {
  deviceId: string;
  label: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString() : "Never";

/**
 * Settings card for the device-bound mobile/tablet PIN. Setup and change both
 * require the current password — the server only stores a bcrypt hash and the
 * device keeps just its random deviceId.
 */
export default function PinSettingsCard() {
  const { currentUser } = useAuth();
  const [thisDevice, setThisDevice] = useState<PinDevice | null>(() => getPinDevice());
  const [devices, setDevices] = useState<PinDeviceInfo[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [label, setLabel] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadDevices = useCallback(async () => {
    try {
      const data = await api.get("/api/auth/pin/devices");
      const list: PinDeviceInfo[] = data?.devices || [];
      setDevices(list);
      // Drop the local deviceId if the server no longer recognises it
      // (revoked elsewhere or by a password change).
      const local = getPinDevice();
      if (local && !list.some((d) => d.deviceId === local.deviceId)) {
        clearPinDevice();
        setThisDevice(null);
      }
    } catch {
      // api wrapper shows the error toast
    } finally {
      setLoadingDevices(false);
    }
  }, []);

  useEffect(() => {
    loadDevices();
    const onChanged = () => {
      setThisDevice(getPinDevice());
      loadDevices();
    };
    window.addEventListener(PIN_DEVICES_CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(PIN_DEVICES_CHANGED_EVENT, onChanged);
  }, [loadDevices]);

  const openDialog = () => {
    setPassword("");
    setPin("");
    setConfirmPin("");
    setLabel("");
    setDialogOpen(true);
  };

  const pinsMatch = confirmPin === "" || pin === confirmPin;
  const canSubmit =
    password.length > 0 && pin.length === 6 && confirmPin.length === 6 && pin === confirmPin;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    try {
      const payload: Record<string, string> = { currentPassword: password, pin };
      if (label.trim()) payload.label = label.trim();
      if (thisDevice) payload.deviceId = thisDevice.deviceId;

      const data = await api.post("/api/auth/pin/setup", payload);

      const next: PinDevice = {
        deviceId: data.deviceId,
        email: currentUser?.email || "",
        name: currentUser?.name || "",
      };
      setPinDevice(next);
      setThisDevice(next);
      setDialogOpen(false);
      toast.success(
        thisDevice ? "PIN updated" : "PIN set up — you can now use it to sign in on this device"
      );
      loadDevices();
    } catch {
      // api wrapper shows the error toast
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (deviceId: string) => {
    try {
      await api.delete(`/api/auth/pin/${deviceId}`);
      if (thisDevice?.deviceId === deviceId) {
        clearPinDevice();
        setThisDevice(null);
      }
      toast.success("PIN access removed");
      loadDevices();
    } catch {
      // api wrapper shows the error toast
    }
  };

  const otherDevices = devices.filter((d) => d.deviceId !== thisDevice?.deviceId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Smartphone className="h-5 w-5" />
          Mobile PIN Sign-In
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Set up a 6-digit PIN to sign in quickly on phones and tablets instead of typing your
          password. The PIN is tied to this device and is removed automatically if your password
          changes or after repeated wrong attempts.
        </p>

        {thisDevice ? (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-muted rounded-lg">
            <div className="flex-1">
              <p className="text-sm font-medium">PIN is set up on this device</p>
              <p className="text-xs text-muted-foreground">
                Sign out and use your PIN on the login screen.
              </p>
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" size="sm" onClick={openDialog}>
                Change PIN
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="text-destructive hover:text-destructive"
                onClick={() => handleRevoke(thisDevice.deviceId)}
              >
                Remove PIN
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <Button type="button" onClick={openDialog}>
              <KeyRound className="h-4 w-4 mr-2" />
              Set up PIN on this device
            </Button>
          </div>
        )}

        {!loadingDevices && otherDevices.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Other devices with PIN access</p>
            <div className="divide-y rounded-lg border">
              {otherDevices.map((d) => (
                <div key={d.deviceId} className="flex items-center gap-3 p-3">
                  <Smartphone className="h-4 w-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{d.label || "Unnamed device"}</p>
                    <p className="text-xs text-muted-foreground">
                      Added {formatDate(d.createdAt)} · Last used {formatDate(d.lastUsedAt)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleRevoke(d.deviceId)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{thisDevice ? "Change Mobile PIN" : "Set up Mobile PIN"}</DialogTitle>
            <DialogDescription>
              Confirm your current password, then choose a 6-digit PIN. Avoid repeated or
              sequential digits.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="pinCurrentPassword">Current Password</Label>
              <Input
                id="pinCurrentPassword"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pinDeviceLabel">Device name (optional)</Label>
              <Input
                id="pinDeviceLabel"
                type="text"
                placeholder="e.g. Jane's iPad"
                maxLength={100}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label>New PIN</Label>
              <PinInput value={pin} onChange={setPin} disabled={submitting} />
            </div>

            <div className="space-y-2">
              <Label>Confirm PIN</Label>
              <PinInput value={confirmPin} onChange={setConfirmPin} disabled={submitting} />
              {!pinsMatch && (
                <p className="text-sm text-red-500">PINs do not match</p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canSubmit || submitting}>
                {submitting ? "Saving..." : thisDevice ? "Update PIN" : "Set up PIN"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
